import { html } from 'lit';
import { isSignedIn } from '../shared/api/session.js';
import { isDeveloperSignedIn } from './api/developerSession.js';
import { currentParams } from '../shared/state/urlState.js';
import { ADMIN_PATH, LOGIN_PATH, safeNext } from '../shared/paths.js';
import { ACCOUNTS_PATH, ACCOUNT_PARAM, PROFILE_PATH, SECTIONS, accountPath, isPersonal, sectionOf } from './accounts.js';
import { getAccounts } from './state/context.js';

import '../shared/views/not-found.js';
import './views/account-billing.js';
import './views/account-billing-history.js';
import './views/account-invitations.js';
import './views/account-members.js';
import './views/account-overview.js';
import './views/account-owners.js';
import './views/account-repositories.js';
import './views/account-settings.js';
import './views/account-tokens.js';
import './views/accounts.js';
import './views/covdbg-login.js';
import './views/dashboard.js';
import './views/impersonate.js';
import './views/home.js';
import './views/landing.js';
import './views/login.js';
import './views/login-choose.js';
import './views/login-exchange.js';
import './views/device.js';
import './views/payment.js';
import './views/profile-decisions.js';
import './views/profile-sessions.js';

/**
 * The customer application's pages.
 *
 * This is the bundle every visitor gets. It is deliberately small, and its being a *separate* bundle
 * from the administration application is the point rather than an accident of layout: an end user is
 * never sent the administrative screens, the sourcetype catalogue they are built from, or the query
 * engine behind them.
 *
 * ## Every team in the sidebar
 *
 * The application is about a person and their teams: the personal one every person has, and the ones
 * they created or were invited into. A team's pages are the sections in `accounts.js`, each addressed
 * as `/<section>?account=<id>`, and the shell's sidebar is every team with its pages underneath, so
 * moving between teams is one click and never a switch. `/teams` lists them all. The developer's own
 * page - addresses, the personal lock - is the sidebar's Overview at `/` and `/profile` alike; sessions
 * and the decisions about their runs are under `/profile`, because they are about the person and not
 * about any team.
 *
 * `/` also decides where a sign-in lands: one team and it opens on it, several and it lists them, only
 * the personal one and it says what personal use is and how to create a team - see `landing.js`. An
 * operator holding only a portal credential lands on a page that says where the portal is.
 *
 * ## Two credentials, one origin
 *
 * A **developer** signs in with an identity provider - or, having done that once, by proving an address - and holds a
 * `covdbg_cs_` client session, the same credential their CLI carries. An **operator** signs in inside the portal with a
 * user name and a password. Routes marked `developer` are about the developer's own credential, and an
 * operator holding only a portal account is sent home from them rather than shown a page that can only
 * fail.
 */

/** No groups: this application's sidebar is the person's teams, built by the shell. */
export const NAV_GROUPS = [];

/** The section routes, spelled out one by one so a harness reading a route's render source finds its tag. */
const SECTION_ROUTES = [
   { id: 'overview', render: () => html`<user-account-overview></user-account-overview>` },
   { id: 'members', render: () => html`<user-account-members></user-account-members>` },
   { id: 'repositories', render: () => html`<user-account-repositories></user-account-repositories>` },
   { id: 'tokens', render: () => html`<user-account-tokens></user-account-tokens>` },
   { id: 'billing', render: () => html`<user-account-billing></user-account-billing>` },
   { id: 'settings', render: () => html`<user-account-settings></user-account-settings>` }
].map(entry =>
{
   const section = SECTIONS.find(candidate => candidate.id === entry.id);

   return {
      path: `/${section.id}`,
      title: section.label,
      icon: section.icon,
      developer: true,
      usesRange: false,
      render: entry.render
   };
});

export const ROUTES = [
   {
      path: '/pay',
      title: 'Secure payment',
      anonymous: true,
      fullscreen: true,
      usesRange: false,
      render: match => html`<user-payment .transactionId=${match.query.get('_ptxn') ?? ''}></user-payment>`
   },
   {
      path: '/',
      title: 'Overview',
      description: 'Your covdbg account.',
      icon: 'fa-solid fa-house',
      usesRange: false,

      // Two different pages behind one address, because "your overview" means two different things to
      // the two populations - and a developer arriving at a page about the *portal* would be told
      // nothing about the thing they came to look at.
      render: () => (isDeveloperSignedIn()
         ? html`<user-landing></user-landing>`
         : html`<user-home></user-home>`)
   },
   {
      path: ACCOUNTS_PATH,
      title: 'Teams',
      description: 'The teams you own and the ones you hold a seat in.',
      icon: 'fa-solid fa-people-group',
      developer: true,
      usesRange: false,
      render: () => html`<user-accounts></user-accounts>`
   },
   ...SECTION_ROUTES,
   {
      path: '/billing/history',
      title: 'Billing history',
      developer: true,
      usesRange: false,
      render: () => html`<user-account-billing-history></user-account-billing-history>`
   },
   {
      path: '/members/invitations',
      title: 'Invitations',
      developer: true,
      usesRange: false,
      render: () => html`<user-account-invitations></user-account-invitations>`
   },
   {
      path: '/members/owners',
      title: 'Owners',
      developer: true,
      usesRange: false,
      render: () => html`<user-account-owners></user-account-owners>`
   },
   {
      path: PROFILE_PATH,
      title: 'Your profile',
      description: 'Your account, its sign-ins and your personal use.',
      icon: 'fa-solid fa-user',
      developer: true,
      usesRange: false,
      render: () => html`<user-dashboard></user-dashboard>`
   },
   {
      path: `${PROFILE_PATH}/sessions`,
      title: 'Sessions',
      developer: true,
      usesRange: false,
      render: () => html`<user-profile-sessions></user-profile-sessions>`
   },
   {
      path: `${PROFILE_PATH}/decisions`,
      title: 'Decisions',
      developer: true,
      usesRange: false,
      render: () => html`<user-profile-decisions></user-profile-decisions>`
   },
   {
      path: LOGIN_PATH,
      title: 'Sign in',
      anonymous: true,
      fullscreen: true,
      usesRange: false,

      /*
       * One form, because this application has one population. An operator signs in inside the portal,
       * at `ADMIN_LOGIN_PATH` - the two used to share this page because they shared an origin, and they
       * are being deployed apart, where a shared page would mean the public host serving the operator's
       * sign-in.
       */
      render: () => html`<user-login .next=${safeNext(currentParams().get('next')) ?? '/'}></user-login>`
   },
   {
      /*
       * Where a browser sign-in lands after the provider's redirect, carrying a single-use code that this
       * page spends for the session. Anonymous, because acquiring the session is what it is for.
       */
      path: '/login/exchange',
      title: 'Signing in',
      anonymous: true,
      fullscreen: true,
      usesRange: false,
      render: () => html`<user-login-exchange></user-login-exchange>`
   },
   {
      /*
       * Where a provider sign-in lands when more than one account, or a verified address, is in play: the
       * callback's protected ticket is the credential, so it is anonymous like the page that spends the code.
       */
      path: '/login/choose',
      title: 'Choose an account',
      anonymous: true,
      fullscreen: true,
      usesRange: false,
      render: () => html`<user-login-choose></user-login-choose>`
   },
   {
      /*
       * Where a client's sign-in is completed: the code `covdbg login` showed, the providers (or "Continue
       * as" for a browser that is signed in) and the team step. Anonymous: a visitor starts here, and the
       * page reads the code with the browser session the sign-in leaves behind.
       */
      path: '/device',
      title: 'Sign in your device',
      anonymous: true,
      fullscreen: true,
      usesRange: false,
      render: () => html`<user-device></user-device>`
   },
   {
      /*
       * Where a magic link and a seat invitation land.
       *
       * In the end-user bundle rather than the administration one, and anonymous, because the person
       * clicking it is a developer who may have no portal account at all. Accepting an invitation needs
       * a session, and the page sends them to sign in first where they hold none.
       */
      path: '/login/covdbg',
      title: 'Confirm',
      anonymous: true,
      fullscreen: true,
      usesRange: false,
      render: () => html`<user-covdbg-login></user-covdbg-login>`
   },
   {
      /*
       * Where a borrowed view lands, having crossed from the portal - routinely from another origin.
       * Anonymous, because the code is the authorisation and a session is what it buys.
       */
      path: '/impersonate',
      title: 'Opening',
      anonymous: true,
      fullscreen: true,
      usesRange: false,
      render: () => html`<user-impersonate></user-impersonate>`
   },
   {
      path: '*',
      title: 'Not found',
      usesRange: false,
      render: () => html`<app-not-found></app-not-found>`
   }
];

/**
 * Paths that used to be pages, and the platform's own idea of where a signed-in reader belongs.
 *
 * `/dashboard` is hardcoded by `<app-not-found>`. The `/accounts/<section>` addresses were the account
 * pages before the sidebar became the account's, and `/organizations` was the list before a customer
 * was called a team; a link to one still opens the same page, with the query string carried along.
 */
const REDIRECTS = {
   '/dashboard': '/',
   '/organizations': ACCOUNTS_PATH,
   ...Object.fromEntries(SECTIONS.map(section => [`/accounts/${section.id}`, `/${section.id}`]))
};

export function guard(route, pathname)
{
   const redirect = REDIRECTS[pathname];

   if (redirect)
   {
      return `${redirect}${location.search}`;
   }

   if (route?.anonymous)
   {
      // Already signed in and asking for the sign-in page: go where the link said, or home. The link
      // legitimately names a page of the administration bundle, and `navigate` turns that into a page
      // load rather than a route lookup that would land on the catch-all.
      return pathname === LOGIN_PATH && (isSignedIn() || isDeveloperSignedIn())
         ? afterSignIn()
         : undefined;
   }

   if (!isSignedIn() && !isDeveloperSignedIn())
   {
      return `${LOGIN_PATH}?next=${encodeURIComponent(pathname + location.search)}`;
   }

   // A page about the developer's own accounts, reached with only a portal credential: home says what
   // this application is and where the portal is, which is the answer.
   if (route?.developer && !isDeveloperSignedIn())
   {
      return '/';
   }

   return personalOverview(pathname);
}

/**
 * A personal team has three pages, and a link to one of the others opens its overview.
 *
 * Decided from the list the shell holds, which is loaded once the application is up - so this answers
 * every navigation inside it, and a cold link to `/billing?account=<personal>` is answered by the page
 * itself once it has read the team (see `AccountView.load`). The same sentence in two places rather than
 * an asynchronous guard, because a guard that waited for a request would hold every page behind it.
 */
function personalOverview(pathname)
{
   const section = sectionOf(pathname);

   if (!section || section.personal)
   {
      return undefined;
   }

   const addressed = currentParams().get(ACCOUNT_PARAM);
   const account = addressed ? getAccounts().find(entry => entry.id === addressed) : null;

   return account && isPersonal(account) ? accountPath('overview', account.id) : undefined;
}

/**
 * Where signing in lands, given which credential the visitor now holds.
 *
 * <b>The link is only followed when the credential can actually use it.</b> A developer signs in by
 * proving an address and holds a client session, which the administration bundle does not accept - so
 * following a `?next=` that names `/admin` would bounce them straight back to the sign-in page, which
 * would send them to `/admin` again, forever. It is a real loop and it is reached by the ordinary route:
 * a developer clicks a portal link somebody pasted into a chat.
 *
 * So a destination in the other application is honoured only for the credential that can open it, and
 * everybody else lands on their own front page - where the page they were sent to is at least
 * explained.
 */
function afterSignIn()
{
   const next = safeNext(currentParams().get('next')) ?? '/';
   const administrative = next === ADMIN_PATH || next.startsWith(`${ADMIN_PATH}/`);

   return administrative && !isSignedIn() ? '/' : next;
}

/**
 * Whether a path is this bundle's to route.
 *
 * Administration and the static pricing document use full page loads. Everything else belongs to
 * this router, including the catch-all for a mistyped application URL.
 */
export function owns(pathname)
{
   // Pricing is the public document, so links to it need a full page load.
   const path = pathname.split('#')[0].replace(/\/$/, '');
   return path !== '/pricing' && !(path === ADMIN_PATH || path.startsWith(`${ADMIN_PATH}/`));
}

/**
 * The static part of the sidebar: nothing.
 *
 * The shell builds the sidebar from the account the application is about - see `user-root.js` - so no
 * route is a fixed entry. Exported because the harnesses read it.
 */
export const NAVIGATION = [];

/** The same assertions the administration table makes, on a table small enough to read at a glance. */
export function assertRoutes()
{
   const paths = new Set();

   for (const route of ROUTES)
   {
      if (route.path.includes(':'))
      {
         throw new Error(
            `Route "${route.path}" carries a path parameter. A page's subject is a named query `
            + 'parameter here, so that no address holds a value a reader has to know the shape of.');
      }

      if (paths.has(route.path))
      {
         throw new Error(`Route "${route.path}" is declared twice; the first one would always win.`);
      }

      paths.add(route.path);

      if (!owns(route.path) && route.path !== '*')
      {
         throw new Error(
            `Route "${route.path}" is not a path this bundle owns, so the router would leave for it `
            + 'before it could ever render.');
      }
   }
}

assertRoutes();
